import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Message } from '../types';
import { v4 as uuidv4 } from 'uuid';

// Enhanced Message interface
interface ExtendedMessage extends Message {
  id: string;
  timestamp: number;
  feedback?: 'positive' | 'negative';
  regenerationCount?: number;
}

interface ChatState {
  messages: ExtendedMessage[];
  sendMessage: (content: string) => Promise<void>;
  updateMessageFeedback: (messageId: string, feedback: 'positive' | 'negative') => void;
  regenerateMessage: (messageId: string) => Promise<void>;
  clearConversation: () => void;
}

// Stoic wisdom responses with more context and variation
const stoicResponses = [
  {
    content: `Recuerda las palabras de Epicteto: "No son las cosas las que nos perturban, sino nuestra interpretación de ellas". Parece que estás luchando con algo que está fuera de tu control directo. 

Reflexiona: 
- ¿Qué parte de esta situación puedes influenciar?
- ¿Qué lección de resiliencia puede extraer de este desafío?

La verdadera sabiduría radica en aceptar lo inevitable y concentrarte en tu respuesta interna.`,
    type: 'reflection'
  },
  {
    content: `Marco Aurelio nos recuerda: "Eres un alma que lleva un cuerpo. No dejes que tus preocupaciones temporales oscurezcan tu paz interior".

Práctica de autocuidado estoico:
1. Identifica tus pensamientos negativos
2. Cuestiona su validez objetivamente
3. Enfócate en lo que puedes hacer, no en lo que no

El estoicismo no busca eliminar las emociones, sino procesarlas con claridad y propósito.`,
    type: 'guidance'
  },
  {
    content: `El estoicismo nos enseña que la libertad verdadera viene de la aceptación y la acción deliberada.

Ejercicio práctico:
- Divide tu preocupación actual en dos categorías:
  a) Lo que puedes controlar
  b) Lo que está fuera de tu control

Concentra tu energía exclusivamente en (a). La paz interior surge cuando sueltas lo que no puedes cambiar.

Recuerda: Cada desafío es una oportunidad para cultivar tu carácter.`,
    type: 'action-plan'
  },
  {
    content: `Séneca decía: "No es que tengamos una vida corta, es que la hacemos corta". ¿Estás invirtiendo tu energía mental de manera sabia?

Reflexión profunda:
- ¿Qué pensamientos te están consumiendo innecesariamente?
- ¿Cómo podrías redesplegar esa energía hacia tu crecimiento personal?

El estoicismo es un arte: transformar los obstáculos en oportunidades de desarrollo.`,
    type: 'philosophical-insight'
  }
];

// Simulated AI response generation with more sophistication
function generateAIResponse(userMessage: string): ExtendedMessage {
  const selectedResponse = stoicResponses[Math.floor(Math.random() * stoicResponses.length)];

  return {
    id: uuidv4(),
    role: 'assistant',
    content: selectedResponse.content,
    timestamp: Date.now(),
    regenerationCount: 0
  };
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      messages: [
        {
          id: uuidv4(),
          role: 'assistant',
          content: 'Hola, soy tu mentor estoico personal. Estoy aquí para ayudarte a desarrollar resiliencia emocional y sabiduría práctica. ¿Qué te preocupa hoy?',
          timestamp: Date.now()
        }
      ],

      sendMessage: async (content: string) => {
        const userMessage: ExtendedMessage = {
          id: uuidv4(),
          role: 'user',
          content,
          timestamp: Date.now()
        };

        set(state => ({
          messages: [...state.messages, userMessage]
        }));

        try {
          // Simulate more realistic AI response
          await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200));

          const aiResponse = generateAIResponse(content);

          set(state => ({
            messages: [...state.messages, aiResponse]
          }));
        } catch (error) {
          console.error('Error sending message:', error);

          // Error handling message
          const errorMessage: ExtendedMessage = {
            id: uuidv4(),
            role: 'assistant',
            content: 'Disculpa, parece que hubo un problema al procesar tu mensaje. La virtud de la paciencia nos ayuda a manejar incluso los contratiempos tecnológicos.',
            timestamp: Date.now()
          };

          set(state => ({
            messages: [...state.messages, errorMessage]
          }));
        }
      },

      updateMessageFeedback: (messageId, feedback) => {
        set(state => ({
          messages: state.messages.map(msg =>
            msg.id === messageId ? { ...msg, feedback } : msg
          )
        }));
      },

      regenerateMessage: async (messageId) => {
        const originalMessage = get().messages.find(msg => msg.id === messageId);

        if (originalMessage && originalMessage.role === 'assistant') {
          try {
            // Remove the original message
            set(state => ({
              messages: state.messages.filter(msg => msg.id !== messageId)
            }));

            // Generate a new response
            await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200));

            const regeneratedResponse = generateAIResponse(originalMessage.content);
            const regeneratedWithCount = {
              ...regeneratedResponse,
              regenerationCount: (originalMessage.regenerationCount || 0) + 1
            };

            set(state => ({
              messages: [...state.messages, regeneratedWithCount]
            }));
          } catch (error) {
            console.error('Error regenerating message:', error);
          }
        }
      },

      clearConversation: () => {
        set({
          messages: [
            {
              id: uuidv4(),
              role: 'assistant',
              content: 'Comenzamos de nuevo. Como dice Séneca, cada nuevo momento es una oportunidad para reiniciarnos. ¿En qué puedo ayudarte hoy?',
              timestamp: Date.now()
            }
          ]
        });
      }
    }),
    {
      name: 'stoic-mentor-chat', // unique name
      partialize: (state) => ({
        messages: state.messages.slice(-20) // Keep only last 20 messages
      })
    }
  )
);